'use client';

import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import {
  Coins,
  Globe,
  PackageCheck,
  PackageOpen,
  SprayCan,
  Tags,
  type LucideIcon,
} from 'lucide-react';

// The news bar at the very top of the public pages (Safras, 24 Oct 2026),
// above the app banner and the header, pinned while the page scrolls. Shown
// to visitors who are not signed in only: PublicHeader leaves it out when it
// has a portal link, and the signed-in area never renders it.
//
// One message on phones, two side by side from 768px, three from 1280px.
// Each row holds for a few seconds, then the next row swipes up into place.
// It pauses under the pointer or keyboard focus, skips the swipe motion for
// visitors who ask for reduced motion (the .feature-ticker-swipe rules in
// globals.css), and never changes height, so the page never shifts. Its
// height is --ticker-h in globals.css; the app banner, the header and anchor
// jumps sit below it through that variable.

interface TickerItem {
  icon: LucideIcon;
  text: string;
  href: string;
}

// Every figure is checked against live data (24 Oct 2026) and stays below
// it, so it remains true as it grows:
//  - 21,663 lines in stock (the home page count); the site says 20,000+.
//  - 764 paid or shipped orders on the site since May 2026.
//  - 300+ brands, the figure the rest of the site uses.
const ITEMS: TickerItem[] = [
  { icon: Coins, text: 'We now accept USDT, with no surcharge', href: '/faq' },
  { icon: Globe, text: 'Shipping to 100+ countries', href: '/shipping-rates' },
  { icon: PackageOpen, text: 'No minimum per fragrance on full bottles', href: '/how-it-works' },
  { icon: SprayCan, text: '20,000+ products in stock', href: '/catalog' },
  { icon: PackageCheck, text: '750+ successful orders', href: '/about' },
  { icon: Tags, text: '300+ designer, niche and Arabic brands', href: '/brands' },
];

// Each row stays 6.5 seconds (Safras, 24 Oct: 4.5 plus 2).
const HOLD_MS = 6500;

/** How many messages a row shows at this width. Must match the md and xl
 *  breakpoints on the slots in Row. */
function perRow(): number {
  if (window.matchMedia('(min-width: 1280px)').matches) return 3;
  if (window.matchMedia('(min-width: 768px)').matches) return 2;
  return 1;
}

/** Three messages from `start` on; the second shows from 768px, the third
 *  from 1280px. */
function Row({ start, incoming }: { start: number; incoming?: boolean }) {
  return (
    // Kept to the header's width (max-w-7xl), so on wide screens the messages
    // line up with the nav instead of drifting to the window edges.
    <div
      aria-hidden={incoming || undefined}
      className="mx-auto grid h-9 w-full max-w-7xl grid-cols-1 md:grid-cols-2 xl:grid-cols-3"
    >
      {[0, 1, 2].map((slot) => {
        const item = ITEMS[(start + slot) % ITEMS.length]!;
        const Icon = item.icon;
        return (
          <Link
            key={slot}
            href={item.href}
            prefetch={false}
            tabIndex={incoming ? -1 : undefined}
            // Sized to be read at a glance on a phone (Safras, 24 Oct): 14px,
            // 15px on computers, regular weight. 13px on the narrowest phones
            // (under 360px), where the longest messages would otherwise be cut.
            className={`h-9 min-w-0 items-center justify-center gap-2 px-3 text-sm font-normal text-[var(--text-primary)] hover:underline max-[359px]:text-[13px] md:text-[15px] ${
              slot === 0 ? 'flex' : slot === 1 ? 'hidden md:flex' : 'hidden xl:flex'
            }`}
          >
            <Icon className="size-[18px] shrink-0 text-[var(--accent)]" aria-hidden="true" />
            <span className="truncate">{item.text}</span>
          </Link>
        );
      })}
    </div>
  );
}

export function FeatureTicker() {
  const [start, setStart] = useState(0);
  // The row swiping in. Only ever set in the browser, after the first
  // render, so the server and the browser start from the same row.
  const [next, setNext] = useState<number | null>(null);
  const startRef = useRef(0);
  const paused = useRef(false);

  useEffect(() => {
    const id = window.setInterval(() => {
      if (paused.current || document.hidden) return;
      // Read the row size now, at the width the visitor actually has, so a
      // row always moves on to the messages it was not showing.
      const step = perRow();
      setNext((current) => current ?? (startRef.current + step) % ITEMS.length);
    }, HOLD_MS);
    return () => window.clearInterval(id);
  }, []);

  const finish = () => {
    if (next === null) return;
    startRef.current = next;
    setStart(next);
    setNext(null);
  };

  return (
    <aside
      aria-label="News from Frags For Less"
      onMouseEnter={() => {
        paused.current = true;
      }}
      onMouseLeave={() => {
        paused.current = false;
      }}
      onFocus={() => {
        paused.current = true;
      }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) paused.current = false;
      }}
      // --accent-soft is see-through, so the page background sits under the
      // tint: without it the page shows through the pinned bar on scroll.
      className="feature-ticker sticky top-0 z-40 h-9 overflow-hidden border-b border-[var(--border)] bg-[var(--background)]"
    >
      <div
        onAnimationEnd={finish}
        className={`flex flex-col bg-[var(--accent-soft)] ${next === null ? '' : 'feature-ticker-swipe'}`}
      >
        <Row key={start} start={start} />
        {next === null ? null : <Row key={next} start={next} incoming />}
      </div>
    </aside>
  );
}
